import { defineComponent as _defineComponent } from 'vue'
import { computed, onUnmounted, ref, watch } from "vue";
import { api } from "@/main";
import Card from "@/components/Card.vue";
import DynamicForm from "@/components/DynamicForm.vue";
import ButtonWithConfirmation from "@/components/ButtonWithConfirmation.vue";
import { Subscription } from "@/api";

type AIPrincipalTokenType = "MCP" | "CONVERSATION";

interface AIPrincipalToken {
  id: string;
  version: number;
  aiPrincipalId: string;
  name: string;
  type: AIPrincipalTokenType;
  createdByUserId: string | null;
  createdByUserName: string | null;
  lastUsedTimestamp: string | null;
  createdTimestamp: string;
  deletedTimestamp: string | null;
}

interface AIPrincipalTokenSecret {
  aiPrincipalToken: AIPrincipalToken;
  rawToken: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AIPrincipalTokens',
  props: ["aiId"],
  setup(__props) {

const props = __props



const tokenSubscription = ref<Subscription | null>(null);
const tokens = ref<AIPrincipalToken[]>([]);
const showCreateDialog = ref(false);
const createTokenType = ref<AIPrincipalTokenType>("MCP");
const createdToken = ref<AIPrincipalTokenSecret | null>(null);
const showCreatedTokenDialog = ref(false);
const createdTokenUrl = computed(() => {
  if (!createdToken.value)
    return "";

  if (createdToken.value.aiPrincipalToken.type === "CONVERSATION")
    return `/api/ai_conversation/${props.aiId}`;

  return `/api/mcp/${props.aiId}`;
});
const createdTokenFullUrl = computed(() => {
  if (!createdTokenUrl.value)
    return "";

  return `${window.location.origin}${createdTokenUrl.value}`;
});
const curlExample = computed(() => {
  if (!createdToken.value || !createdTokenFullUrl.value)
    return "";

  const authorizationHeader = `Authorization: Bearer ${createdToken.value.rawToken}`;
  if (createdToken.value.aiPrincipalToken.type === "CONVERSATION")
    return [
      "curl -sS \\",
      "  -X POST \\",
      `  '${createdTokenFullUrl.value}' \\`,
      `  -H '${authorizationHeader}' \\`,
      "  -H 'Content-Type: application/json' \\",
      "  --data '{\"message\":\"Add bacon to the grocery list\"}'",
    ].join("\n");

  return [
    "curl -sS \\",
    "  -X POST \\",
    `  '${createdTokenFullUrl.value}' \\`,
    `  -H '${authorizationHeader}' \\`,
    "  -H 'Content-Type: application/json' \\",
    "  -H 'MCP-Protocol-Version: 2025-11-25' \\",
    "  --data '{\"jsonrpc\":\"2.0\",\"id\":1,\"method\":\"tools/list\",\"params\":{}}'",
  ].join("\n");
});

const tokenSections: { type: AIPrincipalTokenType; title: string; icon: string; defaultName: string }[] = [
  { type: "MCP", title: "MCP Tokens", icon: "mdi-key-variant", defaultName: "MCP Token" },
  { type: "CONVERSATION", title: "Conversation Tokens", icon: "mdi-message-text-lock-outline", defaultName: "Siri Shortcut" },
];

function createActions(type: AIPrincipalTokenType) {
  return [{
    icon: "mdi-plus",
    click: () => {
      createTokenType.value = type;
      showCreateDialog.value = true;
    },
    color: "primary",
  }];
}

function tokensFor(type: AIPrincipalTokenType): AIPrincipalToken[] {
  return tokens.value.filter(token => token.type === type);
}

function resubscribe(aiId: string) {
  if (tokenSubscription.value != null) {
    api.unsubscribe(tokenSubscription.value);
    tokenSubscription.value = null;
  }

  tokens.value = [];
  tokenSubscription.value = api.subscribe("AIPrincipalToken", "parent:" + aiId, (token: AIPrincipalToken) => {
    const index = tokens.value.findIndex(existingToken => existingToken.id === token.id);
    if (token.deletedTimestamp) {
      if (index >= 0)
        tokens.value.splice(index, 1);
      return;
    }

    if (index >= 0)
      tokens.value.splice(index, 1, token);
    else
      tokens.value.push(token);
  });
}

function createToken(form: { name: string }): Promise<AIPrincipalTokenSecret> {
  return api.rpc("ai", "createAIPrincipalToken", {
    aiPrincipalId: props.aiId,
    name: form.name,
    type: createTokenType.value,
  });
}

function handleCreateSuccess(response: AIPrincipalTokenSecret) {
  createdToken.value = response;
  showCreateDialog.value = false;
  showCreatedTokenDialog.value = true;
}

function revokeToken(token: AIPrincipalToken): Promise<void> {
  return api.rpc("ai", "revokeAIPrincipalToken", {
    aiPrincipalId: props.aiId,
    tokenId: token.id,
  });
}

function copyToken() {
  if (!createdToken.value)
    return;

  if (!navigator.clipboard) {
    api.emitError("Clipboard access is not available in this browser");
    return;
  }

  navigator.clipboard.writeText(createdToken.value.rawToken).catch(() => {
    api.emitError("Could not copy token to clipboard");
  });
}

function formatTimestamp(timestamp: string | null): string {
  if (!timestamp)
    return "Never";

  return new Date(timestamp).toLocaleString();
}

watch(() => props.aiId, (aiId) => {
  if (aiId)
    resubscribe(aiId);
}, { immediate: true });

onUnmounted(() => {
  if (tokenSubscription.value != null)
    api.unsubscribe(tokenSubscription.value);
});

return { __sfc: true,props, tokenSubscription, tokens, showCreateDialog, createTokenType, createdToken, showCreatedTokenDialog, createdTokenUrl, createdTokenFullUrl, curlExample, tokenSections, createActions, tokensFor, resubscribe, createToken, handleCreateSuccess, revokeToken, copyToken, formatTimestamp, Card, DynamicForm, ButtonWithConfirmation }
}

})